/**
 * Wizytówki Google - Kreator Ankiety
 * P0: 7-step wizard (0-6)
 * 
 * KROKI:
 * 0 - Wybór usługi (jednorazowo / opieka)
 * 1 - Dane hodowli (nazwa, czy istnieje?)
 * 2 - Lokalizacja (miasto, adres/obszar)
 * 3 - Kontakt (telefon*, email, www/fb/ig)
 * 4 - Prezentacja (opis, osiągnięcia, godziny)
 * 5 - Zdjęcia (upload)
 * 6 - Zgody + Submit
 * 
 * BRANDING: Biel #FFFFFF, Czerń #0B0B0B, Magenta #E6007A
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Check, ArrowLeft, ArrowRight, Loader2, Upload, X, Image } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '@/utils/api';

const colors = {
  white: '#FFFFFF',
  black: '#0B0B0B',
  magenta: '#E6007A'
};

const STEPS = [
  { id: 0, title: 'Wybór usługi' },
  { id: 1, title: 'Dane hodowli' },
  { id: 2, title: 'Lokalizacja' },
  { id: 3, title: 'Kontakt' },
  { id: 4, title: 'Prezentacja' },
  { id: 5, title: 'Zdjęcia' },
  { id: 6, title: 'Podsumowanie' }
];

export default function WizytowkiWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 0
    service_type: searchParams.get('plan') === 'subscription' ? 'subscription' : 'one_time',
    // Step 1
    kennel_name: '',
    profile_exists: '',
    // Step 2
    city: '',
    location_mode: '',
    address: '',
    service_area_description: '',
    // Step 3
    phone: '',
    email: '',
    website: '',
    // Step 4
    description: '',
    achievements: '',
    contact_hours: '',
    // Step 5
    photos: [],
    // Step 6
    data_confirmed: false,
    manager_consent: false
  });
  
  const update = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Validation per step
  const validateStep = (step) => {
    switch (step) {
      case 0:
        return !!formData.service_type;
      case 1:
        return formData.kennel_name.trim().length >= 2 && !!formData.profile_exists;
      case 2:
        if (!formData.city.trim() || !formData.location_mode) return false;
        if (formData.location_mode === 'address' && !formData.address.trim()) return false;
        if (formData.location_mode === 'service_area' && !formData.service_area_description.trim()) return false;
        return true;
      case 3:
        return formData.phone.trim().length >= 9;
      case 4:
        return formData.description.trim().length >= 20;
      case 5:
        return true; // Photos optional
      case 6:
        return formData.data_confirmed && formData.manager_consent;
      default:
        return true;
    }
  };
  
  const canProceed = validateStep(currentStep);
  
  const goNext = () => {
    if (canProceed && currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSubmit = async () => {
    if (!canProceed) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/wizytowki/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setSubmitted(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Wystąpił błąd. Spróbuj ponownie.');
      }
    } catch (err) {
      toast.error('Błąd połączenia. Sprawdź internet i spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Photo upload handler (mock for now)
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    // For P0, just store file names as placeholders
    // Real upload would go to S3/cloudinary
    const newPhotos = files.map(f => URL.createObjectURL(f));
    update('photos', [...formData.photos, ...newPhotos]);
  };
  
  const removePhoto = (index) => {
    update('photos', formData.photos.filter((_, i) => i !== index));
  };
  
  // Thank you screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="relative">
              <MapPin className="w-12 h-12" style={{ color: colors.black }} strokeWidth={1.5} />
              <Check 
                className="w-6 h-6 absolute -bottom-1 -right-1" 
                style={{ color: colors.magenta }} 
                strokeWidth={3}
              />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold mb-4" style={{ color: colors.black }}>
            Dziękujemy!
          </h1>
          
          <p className="text-gray-600 mb-8">
            Twoje zgłoszenie zostało przyjęte.<br />
            Wkrótce otrzymasz instrukcję dodania nas jako menedżera profilu Google.
          </p>
          
          <button
            onClick={() => navigate('/wizytowki')}
            className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
            style={{ backgroundColor: colors.magenta }}
          >
            Wróć do strony głównej
          </button>
        </div>
      </div>
    );
  }
  
  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step0ServiceType formData={formData} update={update} />;
      case 1:
        return <Step1KennelData formData={formData} update={update} />;
      case 2:
        return <Step2Location formData={formData} update={update} />;
      case 3:
        return <Step3Contact formData={formData} update={update} />;
      case 4:
        return <Step4Presentation formData={formData} update={update} />;
      case 5:
        return <Step5Photos formData={formData} handlePhotoUpload={handlePhotoUpload} removePhoto={removePhoto} />;
      case 6:
        return <Step6Consents formData={formData} update={update} />;
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-white" style={{ color: colors.black }}>
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-6 py-4">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-6 h-6" style={{ color: colors.black }} strokeWidth={1.5} />
            <span className="font-semibold">Wizytówki Google</span>
          </div>
          
          {/* Progress */}
          <p className="text-sm text-gray-500 mb-2">
            Krok {currentStep + 1} z 7 — {STEPS[currentStep].title}
          </p>
          
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full transition-all duration-300"
              style={{ 
                width: `${((currentStep + 1) / 7) * 100}%`,
                backgroundColor: colors.magenta
              }}
            />
          </div>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-xl mx-auto px-6 py-12">
        {renderStep()}
      </main>
      
      {/* Footer Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100">
        <div className="max-w-xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Back */}
          {currentStep > 0 ? (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Wstecz</span>
            </button>
          ) : (
            <div />
          )}
          
          {/* Next / Submit */}
          {currentStep < 6 ? (
            <button
              onClick={goNext}
              disabled={!canProceed}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all ${
                canProceed 
                  ? 'text-white hover:opacity-90' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              style={canProceed ? { backgroundColor: colors.magenta } : {}}
              data-testid="wizard-next-btn"
            >
              <span>Dalej</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed || submitting}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all ${
                canProceed && !submitting
                  ? 'text-white hover:opacity-90' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              style={canProceed && !submitting ? { backgroundColor: colors.magenta } : {}}
              data-testid="wizard-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Wysyłanie...</span>
                </>
              ) : (
                <span>Wyślij zgłoszenie</span>
              )}
            </button>
          )}
        </div>
      </footer>
      
      {/* Spacer for fixed footer */}
      <div className="h-20" />
    </div>
  );
}

// ==================== STEP COMPONENTS ====================

function Step0ServiceType({ formData, update }) {
  const options = [
    { id: 'one_time', title: 'Jednorazowe przygotowanie', price: '450 zł', desc: 'Założenie i optymalizacja wizytówki' },
    { id: 'subscription', title: 'Wizytówka + Opieka', price: '150 zł/mc', desc: '+ 450 zł za przygotowanie' }
  ];
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Wybierz usługę</h1>
      <p className="text-gray-500 text-center mb-8">Co Cię interesuje?</p>
      
      <div className="space-y-3">
        {options.map((opt) => {
          const isActive = formData.service_type === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => update('service_type', opt.id)}
              className={`w-full text-left p-5 rounded-xl transition-all border-l-4 ${
                isActive 
                  ? 'bg-pink-50' 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
              style={isActive ? { borderColor: colors.magenta } : { borderColor: 'transparent' }}
              data-testid={`service-${opt.id}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{opt.title}</p>
                  <p className="text-sm text-gray-500 mt-1">{opt.desc}</p>
                </div>
                <p className="font-bold" style={isActive ? { color: colors.magenta } : {}}>{opt.price}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step1KennelData({ formData, update }) {
  const existsOptions = [
    { id: 'yes', label: 'Tak, mam wizytówkę' },
    { id: 'no', label: 'Nie, nie mam' },
    { id: 'unknown', label: 'Nie wiem' }
  ];
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Dane hodowli</h1>
      <p className="text-gray-500 text-center mb-8">Podstawowe informacje</p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Nazwa hodowli</label>
          <input
            type="text"
            value={formData.kennel_name}
            onChange={(e) => update('kennel_name', e.target.value)}
            placeholder="np. Hodowla Złote Łapy"
            maxLength={100}
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            style={{ focusRingColor: colors.magenta }}
            data-testid="input-kennel-name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Czy masz już wizytówkę Google?</label>
          <div className="space-y-2">
            {existsOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => update('profile_exists', opt.id)}
                className={`w-full text-left p-4 rounded-xl transition-all ${
                  formData.profile_exists === opt.id
                    ? 'bg-pink-50 border-l-4'
                    : 'bg-gray-50 hover:bg-gray-100 border-l-4 border-transparent'
                }`}
                style={formData.profile_exists === opt.id ? { borderColor: colors.magenta } : {}}
                data-testid={`profile-exists-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Step2Location({ formData, update }) {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Lokalizacja</h1>
      <p className="text-gray-500 text-center mb-8">Gdzie znajduje się hodowla?</p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Miasto</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => update('city', e.target.value)}
            placeholder="np. Warszawa"
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            data-testid="input-city"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Tryb lokalizacji</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'address', label: 'Adres' },
              { id: 'service_area', label: 'Obszar działania' }
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => update('location_mode', opt.id)}
                className={`p-4 rounded-xl font-medium transition-all ${
                  formData.location_mode === opt.id
                    ? 'text-white'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={formData.location_mode === opt.id ? { backgroundColor: colors.magenta } : {}}
                data-testid={`location-mode-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        {formData.location_mode === 'address' && (
          <div>
            <label className="block text-sm font-medium mb-2">Adres</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="np. ul. Przykładowa 10, 00-001 Warszawa"
              className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
              data-testid="input-address"
            />
          </div>
        )}
        
        {formData.location_mode === 'service_area' && (
          <div>
            <label className="block text-sm font-medium mb-2">Opis obszaru działania</label>
            <textarea
              value={formData.service_area_description}
              onChange={(e) => update('service_area_description', e.target.value)}
              placeholder="np. Warszawa i okolice do 50 km"
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 resize-none"
              data-testid="input-service-area"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Step3Contact({ formData, update }) {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Kontakt</h1>
      <p className="text-gray-500 text-center mb-8">Jak klienci mogą się z Tobą skontaktować?</p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Telefon <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+48 123 456 789"
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            data-testid="input-phone"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Email <span className="text-gray-400 font-normal">(opcjonalnie)</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="kontakt@twoja-hodowla.pl"
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            data-testid="input-email"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Strona WWW lub social media <span className="text-gray-400 font-normal">(opcjonalnie)</span>
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://facebook.com/twoja-hodowla"
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            data-testid="input-website"
          />
        </div>
      </div>
    </div>
  );
}

function Step4Presentation({ formData, update }) {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Prezentacja</h1>
      <p className="text-gray-500 text-center mb-8">Opowiedz o swojej hodowli</p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Opis hodowli <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Opisz swoją hodowlę: jakie rasy, jak długo działasz, co wyróżnia Twoją hodowlę..."
            rows={4}
            maxLength={1000}
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 resize-none"
            data-testid="input-description"
          />
          <p className="text-xs text-gray-400 mt-1">Min. 20 znaków</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Osiągnięcia hodowlane <span className="text-gray-400 font-normal">(opcjonalnie)</span>
          </label>
          <textarea
            value={formData.achievements}
            onChange={(e) => update('achievements', e.target.value)}
            placeholder="np. Championy, wyróżnienia, certyfikaty..."
            rows={2}
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 resize-none"
            data-testid="input-achievements"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            Godziny kontaktu <span className="text-gray-400 font-normal">(opcjonalnie)</span>
          </label>
          <input
            type="text"
            value={formData.contact_hours}
            onChange={(e) => update('contact_hours', e.target.value)}
            placeholder="np. Pon-Pt 9:00-18:00"
            className="w-full px-4 py-3 bg-gray-50 rounded-xl focus:outline-none focus:ring-2"
            data-testid="input-contact-hours"
          />
        </div>
      </div>
    </div>
  );
}

function Step5Photos({ formData, handlePhotoUpload, removePhoto }) {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Zdjęcia</h1>
      <p className="text-gray-500 text-center mb-8">Dodaj zdjęcia psów i hodowli</p>
      
      <div className="space-y-6">
        {/* Upload area */}
        <label className="block cursor-pointer">
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors">
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <p className="font-medium mb-1">Kliknij, aby dodać zdjęcia</p>
            <p className="text-sm text-gray-500">JPG, PNG do 10MB</p>
          </div>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
            data-testid="input-photos"
          />
        </label>
        
        {/* Photo previews */}
        {formData.photos.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {formData.photos.map((photo, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                <img src={photo} alt={`Zdjęcie ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {formData.photos.length === 0 && (
          <p className="text-sm text-gray-400 text-center">
            Zdjęcia są opcjonalne, ale pomagają w promocji hodowli.
          </p>
        )}
      </div>
    </div>
  );
}

function Step6Consents({ formData, update }) {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-center">Podsumowanie</h1>
      <p className="text-gray-500 text-center mb-8">Potwierdź i wyślij zgłoszenie</p>
      
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-gray-50 rounded-xl p-5 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-500">Usługa</span>
            <span className="font-medium">
              {formData.service_type === 'subscription' ? 'Wizytówka + Opieka' : 'Jednorazowe przygotowanie'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Hodowla</span>
            <span className="font-medium">{formData.kennel_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Miasto</span>
            <span className="font-medium">{formData.city}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Telefon</span>
            <span className="font-medium">{formData.phone}</span>
          </div>
        </div>
        
        {/* Consents */}
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.data_confirmed}
              onChange={(e) => update('data_confirmed', e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
              data-testid="checkbox-data-confirmed"
            />
            <span className="text-sm">
              Potwierdzam, że podane dane są prawidłowe i kompletne.
            </span>
          </label>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.manager_consent}
              onChange={(e) => update('manager_consent', e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
              data-testid="checkbox-manager-consent"
            />
            <span className="text-sm">
              Wyrażam zgodę na działanie ApisAI jako menedżera mojego profilu Google Business.
            </span>
          </label>
        </div>
        
        {/* Info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-sm text-gray-600">
            Po wysłaniu zgłoszenia otrzymasz instrukcję dodania nas jako menedżera Twojego profilu Google.
          </p>
        </div>
      </div>
    </div>
  );
}
